import { useEffect, useState } from 'preact/hooks';
import { get } from '../../api';
import type { PipelineStats } from '../../types';
import { LoadingSpinner } from '../shared/LoadingSpinner';

function fmt(n: number): string {
  return n >= 1000 ? n.toLocaleString() : String(n);
}

function pct(part: number, total: number): string {
  if (total === 0) return '0';
  return Math.round((part / total) * 100) + '%';
}

export function PipelineFunnel() {
  const [stats, setStats] = useState<PipelineStats | null>(null);

  useEffect(() => {
    get<PipelineStats>('/api/stats').then(setStats).catch(console.error);
  }, []);

  if (!stats) return <LoadingSpinner />;

  const seedUnprocessed = stats.seeds_new;
  const seedProcessed = stats.seeds_processed;
  const seedTotal = stats.seeds_total || 1;
  const seedToBrief = seedTotal > 0 ? ((stats.briefs_total / seedTotal) * 100).toFixed(1) + '%' : '0%';

  const briefsTotal = stats.briefs_total || 1;
  const briefToDraft = briefsTotal > 0 ? ((stats.drafts_total / briefsTotal) * 100).toFixed(0) + '%' : '0%';

  const draftsTotal = stats.drafts_total || 1;
  const draftToPub = draftsTotal > 0 ? ((stats.drafts_published / draftsTotal) * 100).toFixed(0) + '%' : '0%';

  return (
    <div class="pipeline">
      {/* Seeds */}
      <div>
        <div class="stage-chrome">
          <div class="stage-endcap" style="background:var(--orange)"></div>
          <div class="stage-bar" style="background:var(--orange)">
            <span class="stage-name">Seeds</span>
            {seedUnprocessed > 100 && <span class="stage-alert-tag">BACKLOG</span>}
          </div>
        </div>
        <div class="stage-body">
          <div class="stage-count" style="color:var(--orange)">{fmt(stats.seeds_total)}</div>
          <div class="div-bar">
            <div class="div-seg seg-unprocessed" style={{ width: pct(seedUnprocessed, seedTotal) }}></div>
            <div class="div-seg seg-processed" style={{ width: pct(seedProcessed, seedTotal) }}></div>
          </div>
          <div class="stage-breakdown">
            <span class="bd-red">{pct(seedUnprocessed, seedTotal)} unprocessed</span>
            <span class="bd-green">{pct(seedProcessed, seedTotal)} done</span>
          </div>
        </div>
      </div>

      <div class="connector">
        <span class="connector-arrow">›</span>
        <span class={`connector-rate${parseFloat(seedToBrief) < 2 ? ' warn' : ''}`}>{seedToBrief}</span>
      </div>

      {/* Briefs */}
      <div>
        <div class="stage-chrome">
          <div class="stage-endcap" style="background:var(--lilac)"></div>
          <div class="stage-bar" style="background:var(--lilac)">
            <span class="stage-name">Briefs</span>
          </div>
        </div>
        <div class="stage-body">
          <div class="stage-count" style="color:var(--lilac)">{fmt(stats.briefs_total)}</div>
          <div class="div-bar">
            <div class="div-seg seg-ready" style={{ width: pct(stats.briefs_ready, briefsTotal) }}></div>
            <div class="div-seg seg-draft" style={{ width: pct(stats.briefs_in_writing, briefsTotal) }}></div>
            <div class="div-seg seg-staged" style={{ width: pct(stats.briefs_staged, briefsTotal) }}></div>
          </div>
          <div class="stage-breakdown">
            <span class="bd-blue">{pct(stats.briefs_ready, briefsTotal)} ready</span>
            <span class="bd-gold">{pct(stats.briefs_staged, briefsTotal)} staged</span>
          </div>
        </div>
      </div>

      <div class="connector">
        <span class="connector-arrow">›</span>
        <span class="connector-rate">{briefToDraft}</span>
      </div>

      {/* Drafts */}
      <div>
        <div class="stage-chrome">
          <div class="stage-endcap" style="background:var(--blue)"></div>
          <div class="stage-bar" style="background:var(--blue)">
            <span class="stage-name">Drafts</span>
          </div>
        </div>
        <div class="stage-body">
          <div class="stage-count" style="color:var(--blue)">{fmt(stats.drafts_total)}</div>
          <div class="div-bar">
            <div class="div-seg seg-draft" style={{ width: pct(stats.drafts_draft, draftsTotal) }}></div>
            <div class="div-seg seg-staged" style={{ width: pct(stats.drafts_staged, draftsTotal) }}></div>
          </div>
          <div class="stage-breakdown">
            <span class="bd-amber">{pct(stats.drafts_draft, draftsTotal)} writing</span>
            <span class="bd-gold">{pct(stats.drafts_staged, draftsTotal)} staged</span>
          </div>
        </div>
      </div>

      <div class="connector">
        <span class="connector-arrow">›</span>
        <span class={`connector-rate${stats.drafts_published === 0 ? ' zero' : ''}`}>{draftToPub}</span>
      </div>

      {/* Published */}
      <div>
        <div class="stage-chrome">
          <div class="stage-endcap" style="background:var(--eggplant)"></div>
          <div class="stage-bar" style="background:var(--eggplant)">
            <span class="stage-name" style="color:var(--lilac)">Published</span>
          </div>
        </div>
        <div class="stage-body">
          <div class="stage-count" style="color:var(--text-dim)">{fmt(stats.drafts_published)}</div>
          <div class="div-bar">
            <div class="div-seg" style="width:100%;background:#1a1a1a"></div>
          </div>
          <div class="stage-breakdown">
            <span class="bd-muted">{stats.drafts_staged} staged · awaiting review</span>
          </div>
        </div>
      </div>
    </div>
  );
}
